import { PrismTransaction } from '@repo/services';

/**
 * Minimal shape `getAreaUnit`/`getAreaValueDisplay`/`getAreaValueInAcres` actually need — just
 * `areaUnit`. Any object with that field works (`PrismTransaction` or the PRM-520
 * `AdaptedTransaction` view of a `TransactionTableRow` both qualify).
 */
export type AreaUnitBearer = { areaUnit?: AreaUnit | string };

/**
 * Supported area units for transaction acreage display.
 * Values are persisted in the database (PrismTransaction.AreaUnit), so do not change strings.
 */
export type AreaUnit = 'acres' | 'hectares';

/**
 * The constant for converting hectares to acres (1 hectare ≈ 2.47105 acres).
 */
export const ACRES_PER_HECTARE = 2.47105;

/**
 * Dropdown options for the area unit selector, providing 'acres' and 'hectares' as selectable values with display labels.
 */
export const AREA_UNIT_OPTIONS: { value: AreaUnit; label: string }[] = [
  { value: 'acres', label: 'Acres' },
  { value: 'hectares', label: 'Hectares' },
];

/**
 * Resolves the area unit for a transaction. Defaults to 'acres' when the field
 * is missing (e.g. legacy transactions or when the API hasn't been migrated yet).
 */
export const getAreaUnit = (transaction: { areaUnit?: AreaUnit | string } | undefined | null): AreaUnit => {
  const raw = transaction?.areaUnit?.toString().toLowerCase();
  return raw === 'hectares' ? 'hectares' : 'acres';
};

/**
 * Acreage in the database is always stored in acres (legacy invariant).
 * This converts the stored acre value to whatever unit the transaction is configured to display.
 */
export const convertAcresToUnit = (acres: number | undefined, unit: AreaUnit): number | undefined => {
  if (acres == null) return acres;
  return unit === 'hectares' ? acres / ACRES_PER_HECTARE : acres;
};

/**
 * Inverse of convertAcresToUnit — converts a user-facing value (in the transaction's unit)
 * back to acres for persistence.
 */
export const convertUnitToAcres = (value: number | undefined, unit: AreaUnit): number | undefined => {
  if (value == null) return value;
  return unit === 'hectares' ? value * ACRES_PER_HECTARE : value;
};

/**
 * Returns the human-readable label for an area unit, e.g. for use in form field labels.
 *
 * @param unit - The area unit to label.
 * @param plural - Whether to return the plural form (default `true`).
 * @returns `'Acres'`/`'Acre'` or `'Hectares'`/`'Hectare'` depending on `unit` and `plural`.
 */
export const getAreaUnitLabel = (unit: AreaUnit, plural = true): string => {
  if (unit === 'hectares') return plural ? 'Hectares' : 'Hectare';
  return plural ? 'Acres' : 'Acre';
};

/**
 * Returns the short abbreviation for an area unit, e.g. for compact display next to a numeric value.
 *
 * @param unit - The area unit to abbreviate.
 * @returns `'ha'` for hectares, `'ac'` for acres.
 */
export const getAreaUnitShortLabel = (unit: AreaUnit): string => (unit === 'hectares' ? 'ha' : 'ac');

/**
 * Resolves the display area unit for a `PrismTransaction`, delegating to `getAreaUnit`.
 *
 * @param transaction - The transaction whose configured area unit should be resolved.
 * @returns The transaction's `AreaUnit`, defaulting to `'acres'`.
 */
export const getTransactionAreaUnit = (transaction: PrismTransaction | undefined | null): AreaUnit =>
  getAreaUnit(transaction as { areaUnit?: string } | undefined);

/**
 * Display metadata for area values stored in a transaction's configured unit, including the value, resolved unit, acre equivalent, and whether to show the acre equivalent.
 */
export interface AreaValueDisplay {
  value: number | undefined;
  unit: AreaUnit;
  acreEquivalent: number | undefined;
  showAcreEquivalent: boolean;
}

/**
 * Builds display metadata for area values that are stored in the transaction's selected unit.
 * Hectare values are shown as-is, with acres calculated from that stored hectare value.
 */
export const getAreaValueDisplay = (
  value: number | undefined,
  transaction: { areaUnit?: AreaUnit | string } | PrismTransaction | undefined | null,
): AreaValueDisplay => {
  const unit = getAreaUnit(transaction as { areaUnit?: AreaUnit | string } | undefined | null);
  const acreEquivalent = value == null
    ? value
    : unit === 'hectares'
      ? value * ACRES_PER_HECTARE
      : value;

  return {
    value,
    unit,
    acreEquivalent,
    showAcreEquivalent: unit === 'hectares' && value != null,
  };
};

/**
 * Converts an area value in the transaction's configured unit to its acre equivalent, via `getAreaValueDisplay`.
 *
 * @param value - The area value as stored/entered in the transaction's configured unit.
 * @param transaction - The transaction (or an object with `areaUnit`) used to resolve the unit.
 * @returns The value converted to acres, or `undefined` if `value` is nullish.
 */
export const getAreaValueInAcres = (
  value: number | undefined,
  transaction: { areaUnit?: AreaUnit | string } | PrismTransaction | undefined | null,
): number | undefined => getAreaValueDisplay(value, transaction).acreEquivalent;
